import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Client, type Rental, type RentalInput, clientsApi, rentalsApi, sitesApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function ClientsPage() {
  const qc = useQueryClient();
  const clientsQ = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const rentalsQ = useQuery({ queryKey: ["rentals"], queryFn: rentalsApi.list });
  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list });

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-display font-bold">Clients & Rentals</h1>
        <p className="text-ink-600 text-sm mt-1">Lessees and the sites they're renting.</p>
      </header>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-semibold text-ink-900">Clients</h2>
          <NewClientButton onCreated={() => qc.invalidateQueries({ queryKey: ["clients"] })} />
        </div>
        {clientsQ.isLoading && <Loading />}
        {clientsQ.error && <ErrorBox msg={String((clientsQ.error as Error).message)} />}
        {clientsQ.data && (clientsQ.data.clients.length === 0
          ? <EmptyState text="No clients yet. Add a lessee above." />
          : <ClientsTable clients={clientsQ.data.clients} />
        )}
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-semibold text-ink-900">Rentals</h2>
          {clientsQ.data && sitesQ.data && (
            <NewRentalButton
              clients={clientsQ.data.clients}
              sites={sitesQ.data.sites}
              onCreated={() => qc.invalidateQueries({ queryKey: ["rentals"] })}
            />
          )}
        </div>
        {rentalsQ.isLoading && <Loading />}
        {rentalsQ.error && <ErrorBox msg={String((rentalsQ.error as Error).message)} />}
        {rentalsQ.data && (rentalsQ.data.rentals.length === 0
          ? <EmptyState text="No active rentals. Add one above to start tracking." />
          : <RentalsTable rentals={rentalsQ.data.rentals} onChange={() => qc.invalidateQueries({ queryKey: ["rentals"] })} />
        )}
      </section>
    </div>
  );
}

function ClientsTable({ clients }: { clients: Client[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<Client>(
    clients,
    {
      business: (c) => c.businessName,
      contact: (c) => c.contactName,
      email: (c) => c.contactEmail,
      phone: (c) => c.contactPhone,
      added: (c) => c.createdAt,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="Business" sortKey="business" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Contact" sortKey="contact" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Email" sortKey="email" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Phone" sortKey="phone" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Added" sortKey="added" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((c) => (
            <tr key={c.id} className="hover:bg-ink-50/50">
              <td className="px-4 py-3 font-medium text-ink-900">{c.businessName}</td>
              <td className="px-4 py-3 text-ink-600">{c.contactName ?? "—"}</td>
              <td className="px-4 py-3 text-ink-600">
                {c.contactEmail ? <a href={`mailto:${c.contactEmail}`}>{c.contactEmail}</a> : "—"}
              </td>
              <td className="px-4 py-3 text-ink-600">
                {c.contactPhone ? <a href={`tel:${c.contactPhone}`}>{c.contactPhone}</a> : "—"}
              </td>
              <td className="px-4 py-3 text-xs text-ink-500">
                {new Date(c.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function RentalsTable({ rentals, onChange }: { rentals: Rental[]; onChange: () => void }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<Rental>(
    rentals,
    {
      site: (r) => r.siteDomain,
      client: (r) => r.clientName,
      rate: (r) => r.monthlyRate,
      status: (r) => r.status,
      started: (r) => r.startDate,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Client" sortKey="client" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Monthly rate" sortKey="rate" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
            <SortHeader label="Status" sortKey="status" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Started" sortKey="started" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((r) => (
            <RentalRow key={r.id} rental={r} onChange={onChange} />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function RentalRow({ rental, onChange }: { rental: Rental; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (status: string) => rentalsApi.update(rental.id, { status }),
    onSuccess: onChange,
  });
  const tone: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    trial: "bg-blue-100 text-blue-800",
    paused: "bg-accent-100 text-accent-700",
    cancelled: "bg-ink-100 text-ink-700",
  };
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-3 text-ink-900">{rental.siteDomain ?? rental.siteId.slice(0, 8) + "…"}</td>
      <td className="px-4 py-3 text-ink-700">{rental.clientName ?? rental.clientId.slice(0, 8) + "…"}</td>
      <td className="px-4 py-3 text-right tabular-nums font-semibold">
        ${rental.monthlyRate.toFixed(2)}/mo
      </td>
      <td className="px-4 py-3">
        <select
          value={rental.status}
          onChange={(e) => update.mutate(e.target.value)}
          disabled={update.isPending}
          className={`px-2 py-0.5 text-xs rounded-md font-medium border-none ${tone[rental.status] ?? tone.cancelled}`}
        >
          <option value="trial">trial</option>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="cancelled">cancelled</option>
        </select>
      </td>
      <td className="px-4 py-3 text-xs text-ink-500">
        {rental.startDate ? new Date(rental.startDate).toLocaleDateString() : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        {update.isPending && <span className="text-xs text-ink-500">saving…</span>}
      </td>
    </tr>
  );
}

function NewClientButton({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 text-sm bg-ink-900 text-white rounded-md hover:bg-ink-800 font-medium"
      >+ New client</button>
      {open && <ClientForm onClose={() => setOpen(false)} onCreated={() => { setOpen(false); onCreated(); }} />}
    </>
  );
}

function ClientForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const m = useMutation({
    mutationFn: () => clientsApi.create({ businessName, contactName, contactEmail, contactPhone, notes }),
    onSuccess: onCreated,
  });

  return (
    <Modal title="Add client" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }} className="space-y-3">
        <FormField label="Business name *">
          <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} required autoFocus className={inputCls} />
        </FormField>
        <FormField label="Contact name">
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Email">
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Phone">
          <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} />
        </FormField>
        <ModalActions submitting={m.isPending} error={m.error ? String((m.error as Error).message) : null} onClose={onClose} submitLabel="Add client" />
      </form>
    </Modal>
  );
}

function NewRentalButton({ clients, sites, onCreated }: {
  clients: Client[];
  sites: Array<{ id: string; domain: string }>;
  onCreated: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (clients.length === 0 || sites.length === 0) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 text-sm bg-ink-900 text-white rounded-md hover:bg-ink-800 font-medium"
      >+ New rental</button>
      {open && <RentalForm clients={clients} sites={sites} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); onCreated(); }} />}
    </>
  );
}

function RentalForm({ clients, sites, onClose, onCreated }: {
  clients: Client[];
  sites: Array<{ id: string; domain: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [siteId, setSiteId] = useState(sites[0]?.id ?? "");
  const [clientId, setClientId] = useState(clients[0]?.id ?? "");
  const [monthlyRate, setMonthlyRate] = useState(500);
  const [status, setStatus] = useState("trial");
  const [paymentMethod, setPaymentMethod] = useState("");
  const m = useMutation({
    mutationFn: (): Promise<{ ok: boolean; id: string }> => {
      const payload: RentalInput = { siteId, clientId, monthlyRate, status, paymentMethod };
      return rentalsApi.create(payload);
    },
    onSuccess: onCreated,
  });
  return (
    <Modal title="Add rental" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }} className="space-y-3">
        <FormField label="Site *">
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required className={inputCls}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.domain}</option>)}
          </select>
        </FormField>
        <FormField label="Client (lessee) *">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} required className={inputCls}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.businessName}</option>)}
          </select>
        </FormField>
        <FormField label="Monthly rate (USD) *">
          <input type="number" min={0} step={50} value={monthlyRate} onChange={(e) => setMonthlyRate(parseFloat(e.target.value))} required className={inputCls} />
        </FormField>
        <FormField label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            <option value="trial">trial</option>
            <option value="active">active</option>
          </select>
        </FormField>
        <FormField label="Payment method">
          <input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} placeholder="e.g. Stripe sub_xxx, ACH, wire" className={inputCls} />
        </FormField>
        <ModalActions submitting={m.isPending} error={m.error ? String((m.error as Error).message) : null} onClose={onClose} submitLabel="Add rental" />
      </form>
    </Modal>
  );
}

const inputCls = "w-full px-3 py-2 border-2 border-ink-100 rounded-lg focus:border-accent focus:outline-none text-sm";

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-ink-500 font-medium mb-1">{label}</span>
      {children}
    </label>
  );
}

function ModalActions({ submitting, error, onClose, submitLabel }: {
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  submitLabel: string;
}) {
  return (
    <>
      {error && <p className="text-red-700 text-sm">{error}</p>}
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={submitting}
                className="flex-1 bg-accent hover:bg-accent-600 disabled:bg-accent-100 text-white font-semibold py-2.5 rounded-lg transition-colors">
          {submitting ? "Saving…" : submitLabel}
        </button>
        <button type="button" onClick={onClose}
                className="px-4 py-2.5 border border-ink-200 text-ink-700 hover:bg-ink-50 rounded-lg">
          Cancel
        </button>
      </div>
    </>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-ink-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-900 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>;
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
      <strong>Failed to load.</strong> {msg}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
      <p className="text-ink-500 text-sm">{text}</p>
    </div>
  );
}
